<script>
    import { onMount } from 'svelte';
    import Layout from '../components/Layout.svelte';

    let posToSync = [];
    let currentIndex = 0;
    let syncComplete = false;
    let error = null;
    let syncResults = [];

    onMount(() => {
        // Get POs from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const posParam = urlParams.get('pos');
        if (posParam) {
            try {
                posToSync = JSON.parse(decodeURIComponent(posParam));
                startSync();
            } catch (err) {
                error = 'Failed to parse purchase orders data';
                console.error(err);
            }
        } else {
            error = 'No purchase orders provided for sync';
        }
    });

    async function startSync() {
        for (let i = 0; i < posToSync.length; i++) {
            currentIndex = i;
            const po = posToSync[i];
            
            try {
                console.log(`Syncing PO ${i + 1}/${posToSync.length}: ${po.name}`);
                
                const response = await fetch('/api/xero/create-purchase-order', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(po)
                });

                // Log the full response for debugging
                const responseText = await response.text();
                console.log('Raw response:', responseText);
                
                let errorMessage = 'Failed to sync PO';
                let errorDetails = null;
                let responseData = null;
                
                // Try to parse the response as JSON
                try {
                    responseData = JSON.parse(responseText);
                    console.log('Parsed response data:', responseData);
                } catch (parseError) {
                    console.error('Error parsing response:', parseError);
                }
                
                if (!response.ok) {
                    // Get the message and details from the response if possible
                    if (responseData) {
                        if (responseData.message) {
                            errorMessage = responseData.message;
                        }
                        
                        // Extract error details
                        errorDetails = responseData.details || 'No specific error details provided';
                        console.log('Error details found:', errorDetails);
                    } else {
                        errorDetails = 'Server error with no details. Please check server logs.';
                    }
                    
                    throw new Error(errorMessage, { cause: errorDetails });
                }

                syncResults.push({
                    po,
                    success: true,
                    purchaseOrderNumber: responseData.purchaseOrderNumber
                });
                
                // Force UI update
                syncResults = [...syncResults];
                
            } catch (err) {
                console.error(`Error syncing PO ${po.name}:`, err);
                
                syncResults.push({
                    po,
                    success: false,
                    error: err.message || 'Unknown error',
                    errorDetails: err.cause || 'No error details available'
                });
                // Force UI update
                syncResults = [...syncResults];
            }

            // Add a small delay between requests to avoid overwhelming the API
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        syncComplete = true;
    }

    function handleBackClick() {
        window.location.href = '/sync-pos';
    }
</script>

<Layout>
    <div class="p-6">
        <h1 class="text-2xl font-semibold mb-6">Sync Progress</h1>

        {#if error}
            <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
                <button 
                    class="ml-4 underline"
                    on:click={handleBackClick}
                >
                    Go Back
                </button>
            </div>
        {:else}
            <div class="mb-6">
                <div class="text-lg mb-2">
                    Progress: {currentIndex + 1} of {posToSync.length} POs
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                        class="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                        style="width: {((currentIndex + 1) / posToSync.length) * 100}%"
                    ></div>
                </div>
            </div>

            <div class="space-y-4">
                {#each syncResults as result}
                    <div class="border rounded-lg p-4 {result.success ? 'bg-green-50' : 'bg-red-50'}">
                        <div class="font-medium">
                            {result.po.name}
                            {#if result.success}
                                <span class="text-green-600 ml-2">✓</span>
                            {:else}
                                <span class="text-red-600 ml-2">✗</span>
                            {/if}
                        </div>
                        <div class="text-sm text-gray-600">
                            {result.po.vendor}
                        </div>
                        {#if result.success}
                            <div class="text-sm text-green-600">
                                Created in Xero: {result.purchaseOrderNumber}
                            </div>
                        {:else}
                            <div class="text-sm text-red-600 mb-2">
                                Error: {result.error}
                            </div>
                            
                            {#if result.errorDetails}
                                <div class="text-sm font-medium mt-2 p-3 bg-red-50 border border-red-200 rounded">
                                    <div class="flex items-start">
                                        <svg class="w-5 h-5 text-red-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        <div>
                                            {#if result.errorDetails && typeof result.errorDetails === 'string' && result.errorDetails.includes('TaxType code')}
                                                <span class="font-semibold">Xero Tax Configuration Error:</span>
                                                <p class="mt-1">{result.errorDetails}</p>
                                                <p class="mt-2 text-xs text-gray-700">
                                                    This can be resolved by updating your tax settings in Xero. Go to Settings → Chart of Accounts
                                                    and update the tax settings for the account mentioned in the error.
                                                </p>
                                            {:else}
                                                <span class="font-semibold">Xero Validation Error:</span>
                                                <p class="mt-1">{result.errorDetails}</p>
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                            {/if}
                        {/if}
                    </div>
                {/each}
            </div>

            {#if syncComplete}
                <div class="mt-6 flex justify-center">
                    <button 
                        class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        on:click={handleBackClick}
                    >
                        Back to Purchase Orders
                    </button>
                </div>
            {/if}
        {/if}
    </div>
</Layout> 